import { Box, MenuItem, Select, Stack, Step, StepButton, Stepper, SxProps, Theme, Typography } from "@mui/material";
import React from "react";
import { FormattedMessage } from "react-intl";

import { Questionnaire } from "../../globals/types";
import useSectionIndex from "../../hooks/useSectionIndex";
import { LocalizedContent } from "../../lib/i18n/contentTranslations";

import { NumberIcon } from "./NumberIcon";

type SectionProgress = { answered: number; total: number };

type Props = {
    questionnaire: Questionnaire;
    progress?: SectionProgress[];
};

const SectionsNav: React.FC<Props> = ({ questionnaire, progress }) => {
    const [sectionIndex, setSectionIndex] = useSectionIndex();

    return (
        <Box>
            <SelectNavigation
                questionnaire={questionnaire}
                sectionIndex={sectionIndex}
                onChange={setSectionIndex}
                sx={{ display: { xs: "block", md: "none" } }}
            />
            <StepperNavigation
                questionnaire={questionnaire}
                sectionIndex={sectionIndex}
                onChange={setSectionIndex}
                progress={progress}
                sx={{ display: { xs: "none", md: "block" } }}
            />
        </Box>
    );
};

const StepperNavigation: React.FC<{
    questionnaire: Questionnaire;
    sectionIndex: number;
    onChange: (sectionIndex: number) => void;
    progress?: SectionProgress[];
    sx?: SxProps<Theme>;
}> = ({ questionnaire, sectionIndex, onChange, progress, sx }) => {
    return (
        <Stepper activeStep={sectionIndex} orientation="vertical" nonLinear sx={sx}>
            {questionnaire.sections.map((section, i) => {
                const p = progress?.[i];
                const complete = !!p && p.total > 0 && p.answered === p.total;
                const active = i === sectionIndex;
                return (
                    <Step key={section.id} active={active} completed={complete}>
                        <StepButton
                            disabled={false}
                            onClick={() => onChange(i)}
                            sx={{
                                borderRadius: 1.5,
                                px: 1,
                                py: 0.75,
                                my: 0.25,
                                transition: "background-color 120ms ease",
                                "& .MuiStepLabel-root": { py: 0 },
                                "&:hover": { backgroundColor: "action.hover" },
                                "&:hover .MuiStepLabel-label": { color: "text.primary" },
                                ...(complete && {
                                    "& .MuiStepLabel-iconContainer .MuiSvgIcon-root": {
                                        color: "tokens.border.borderColorSuccess",
                                    },
                                }),
                                ...(active && {
                                    backgroundColor: "action.selected",
                                    "&:hover": { backgroundColor: "action.selected" },
                                    "& .MuiStepLabel-label": { color: "text.primary", fontWeight: 600 },
                                    ...(!complete && {
                                        "& .MuiStepLabel-iconContainer .MuiSvgIcon-root": { color: "text.textBody" },
                                    }),
                                }),
                            }}
                            optional={
                                p ? (
                                    <Typography variant="textXs" color="textTextHelper">
                                        {complete ? (
                                            <FormattedMessage defaultMessage="Complete" />
                                        ) : (
                                            <FormattedMessage
                                                defaultMessage="{answered} of {total} answered"
                                                values={{ answered: p.answered, total: p.total }}
                                            />
                                        )}
                                    </Typography>
                                ) : undefined
                            }
                        >
                            <LocalizedContent translationKey={section.translationKey} fallback={section.name} />
                        </StepButton>
                    </Step>
                );
            })}
        </Stepper>
    );
};

const SelectNavigation: React.FC<{
    questionnaire: Questionnaire;
    sectionIndex: number;
    onChange: (sectionIndex: number) => void;
    sx?: SxProps<Theme>;
}> = ({ questionnaire, sectionIndex, onChange, sx }) => {
    return (
        <Select
            sx={sx}
            fullWidth
            value={sectionIndex}
            onChange={(e) => {
                const newValue = typeof e.target.value === "string" ? parseInt(e.target.value, 10) : e.target.value;
                onChange(newValue);
            }}
            renderValue={(value) => {
                const section = questionnaire.sections[value];
                return (
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <NumberIcon>{sectionIndex + 1}</NumberIcon>
                        <Typography variant="textMd" color="primary">
                            <LocalizedContent translationKey={section.translationKey} fallback={section.name} />
                        </Typography>
                    </Stack>
                );
            }}
        >
            {questionnaire.sections.map((section, i) => (
                <MenuItem key={section.id} value={i}>
                    <Typography variant="textMd">
                        <LocalizedContent translationKey={section.translationKey} fallback={section.name} />
                    </Typography>
                </MenuItem>
            ))}
        </Select>
    );
};

export default SectionsNav;
